import { useState } from 'react';

export default function ToolsCalculators() {
  // Medical calc state
  const [medDays, setMedDays] = useState('');
  const [medRoom, setMedRoom] = useState('');
  const [medSurgery, setMedSurgery] = useState('');
  const [medEmergency, setMedEmergency] = useState('');
  const [medResult, setMedResult] = useState<number | null>(null);

  // LTC calc state
  const [ltcMonthly, setLtcMonthly] = useState('');
  const [ltcYears, setLtcYears] = useState('');
  const [ltcAffordable, setLtcAffordable] = useState('');
  const [ltcResult, setLtcResult] = useState<{ total: number; gap: number } | null>(null);

  const calcMedical = () => {
    const days = parseFloat(medDays) || 0;
    const room = parseFloat(medRoom) || 0;
    const surgery = parseFloat(medSurgery) || 0;
    const emergency = parseFloat(medEmergency) || 0;
    setMedResult(days * room + surgery + emergency);
  };

  const calcLtc = () => {
    const monthly = parseFloat(ltcMonthly) || 0;
    const years = parseFloat(ltcYears) || 0;
    const affordable = parseFloat(ltcAffordable) || 0;
    const total = monthly * 12 * years;
    const gap = Math.max(0, total - affordable);
    setLtcResult({ total, gap });
  };

  const formatNTD = (n: number) =>
    n >= 10000 ? `${(n / 10000).toFixed(1)} 萬元` : `${n.toLocaleString()} 元`;

  return (
    <section id="calc-section" className="bg-[#F8F5F0] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[#D4A373] font-semibold tracking-widest mb-3" style={{ fontSize: '1rem' }}>
            CALCULATORS
          </p>
          <h2
            className="font-serif font-bold text-[#5C5470]"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.4' }}
          >
            試算工具
          </h2>
          <div className="w-14 h-1 bg-[#D4A373] mx-auto rounded-full mt-4 mb-4"></div>
          <p className="text-gray-600 max-w-lg mx-auto" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>
            試算結果僅供參考，實際保障需求建議與專業顧問討論
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Medical Calculator */}
          <div className="bg-white rounded-3xl p-10 border border-gray-100">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#FF8C69]/15">
                <i className="ri-heart-pulse-line text-[#FF8C69] text-2xl"></i>
              </div>
              <div>
                <h3
                  className="font-serif font-bold text-[#5C5470]"
                  style={{ fontSize: 'clamp(1.25rem, 2vw, 1.625rem)', lineHeight: '1.45' }}
                >
                  醫療費用準備試算
                </h3>
                <p className="text-gray-500 mt-0.5" style={{ fontSize: '1rem' }}>估算一次住院可能的費用</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-[#5C5470] font-medium mb-2" style={{ fontSize: '1.0625rem' }}>
                  預估住院天數（天）
                </label>
                <input
                  type="number"
                  min="0"
                  value={medDays}
                  onChange={(e) => setMedDays(e.target.value)}
                  placeholder="例如：7"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-700 focus:outline-none focus:border-[#D4A373] transition-colors"
                  style={{ fontSize: '1.0625rem' }}
                />
              </div>
              <div>
                <label className="block text-[#5C5470] font-medium mb-2" style={{ fontSize: '1.0625rem' }}>
                  每日病房費（元）
                </label>
                <input
                  type="number"
                  min="0"
                  value={medRoom}
                  onChange={(e) => setMedRoom(e.target.value)}
                  placeholder="例如：3000"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-700 focus:outline-none focus:border-[#D4A373] transition-colors"
                  style={{ fontSize: '1.0625rem' }}
                />
              </div>
              <div>
                <label className="block text-[#5C5470] font-medium mb-2" style={{ fontSize: '1.0625rem' }}>
                  手術與自費項目預估（元）
                </label>
                <input
                  type="number"
                  min="0"
                  value={medSurgery}
                  onChange={(e) => setMedSurgery(e.target.value)}
                  placeholder="例如：50000"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-700 focus:outline-none focus:border-[#D4A373] transition-colors"
                  style={{ fontSize: '1.0625rem' }}
                />
              </div>
              <div>
                <label className="block text-[#5C5470] font-medium mb-2" style={{ fontSize: '1.0625rem' }}>
                  緊急預備金（元）
                </label>
                <input
                  type="number"
                  min="0"
                  value={medEmergency}
                  onChange={(e) => setMedEmergency(e.target.value)}
                  placeholder="例如：20000"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-700 focus:outline-none focus:border-[#D4A373] transition-colors"
                  style={{ fontSize: '1.0625rem' }}
                />
              </div>

              <button
                onClick={calcMedical}
                className="w-full bg-[#FF8C69] text-white py-4 rounded-full font-semibold whitespace-nowrap hover:bg-[#e87a58] transition-all duration-200 hover:scale-[1.02] cursor-pointer"
                style={{ fontSize: '1.1875rem' }}
              >
                <i className="ri-calculator-line mr-2"></i>
                開始試算
              </button>

              {medResult !== null && (
                <div className="bg-[#FF8C69]/10 border border-[#FF8C69]/25 rounded-2xl p-6 text-center">
                  <p className="text-gray-600 mb-2" style={{ fontSize: '1.0625rem' }}>預估醫療費用準備</p>
                  <p className="font-serif font-bold text-[#5C5470]" style={{ fontSize: '2.125rem', lineHeight: '1.2' }}>
                    {formatNTD(medResult)}
                  </p>
                  <p className="text-gray-500 mt-3" style={{ fontSize: '1rem' }}>
                    ※ 此為粗估參考值，實際費用因病況與醫院而異
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* LTC Calculator */}
          <div className="bg-white rounded-3xl p-10 border border-gray-100">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#5C5470]/12">
                <i className="ri-time-line text-[#5C5470] text-2xl"></i>
              </div>
              <div>
                <h3
                  className="font-serif font-bold text-[#5C5470]"
                  style={{ fontSize: 'clamp(1.25rem, 2vw, 1.625rem)', lineHeight: '1.45' }}
                >
                  長期照護費用試算
                </h3>
                <p className="text-gray-500 mt-0.5" style={{ fontSize: '1rem' }}>估算長照總費用與保障缺口</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-[#5C5470] font-medium mb-2" style={{ fontSize: '1.0625rem' }}>
                  每月照護費用（元）
                </label>
                <input
                  type="number"
                  min="0"
                  value={ltcMonthly}
                  onChange={(e) => setLtcMonthly(e.target.value)}
                  placeholder="例如：50000"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-700 focus:outline-none focus:border-[#D4A373] transition-colors"
                  style={{ fontSize: '1.0625rem' }}
                />
              </div>
              <div>
                <label className="block text-[#5C5470] font-medium mb-2" style={{ fontSize: '1.0625rem' }}>
                  預估照護年限（年）
                </label>
                <input
                  type="number"
                  min="0"
                  value={ltcYears}
                  onChange={(e) => setLtcYears(e.target.value)}
                  placeholder="例如：10"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-700 focus:outline-none focus:border-[#D4A373] transition-colors"
                  style={{ fontSize: '1.0625rem' }}
                />
              </div>
              <div>
                <label className="block text-[#5C5470] font-medium mb-2" style={{ fontSize: '1.0625rem' }}>
                  家庭可負擔金額（元）
                </label>
                <input
                  type="number"
                  min="0"
                  value={ltcAffordable}
                  onChange={(e) => setLtcAffordable(e.target.value)}
                  placeholder="例如：1000000"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-700 focus:outline-none focus:border-[#D4A373] transition-colors"
                  style={{ fontSize: '1.0625rem' }}
                />
              </div>
              <div className="bg-[#F8F5F0] rounded-xl p-4">
                <p className="text-gray-500 flex items-start gap-2" style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                  <i className="ri-information-line text-[#D4A373] mt-0.5 flex-shrink-0"></i>
                  保障缺口估算 = 長照總費用 − 家庭可負擔金額
                </p>
              </div>

              <button
                onClick={calcLtc}
                className="w-full bg-[#5C5470] text-white py-4 rounded-full font-semibold whitespace-nowrap hover:bg-[#4a4360] transition-all duration-200 hover:scale-[1.02] cursor-pointer"
                style={{ fontSize: '1.1875rem' }}
              >
                <i className="ri-calculator-line mr-2"></i>
                開始試算
              </button>

              {ltcResult !== null && (
                <div className="bg-[#5C5470]/8 border border-[#5C5470]/20 rounded-2xl p-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-gray-600 mb-1" style={{ fontSize: '1rem' }}>長照總費用</p>
                      <p className="font-serif font-bold text-[#5C5470]" style={{ fontSize: '1.625rem', lineHeight: '1.2' }}>
                        {formatNTD(ltcResult.total)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-600 mb-1" style={{ fontSize: '1rem' }}>保障缺口</p>
                      <p
                        className="font-serif font-bold"
                        style={{ fontSize: '1.625rem', lineHeight: '1.2', color: ltcResult.gap > 0 ? '#FF8C69' : '#5C5470' }}
                      >
                        {ltcResult.gap > 0 ? formatNTD(ltcResult.gap) : '無缺口'}
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-500 mt-4 text-center" style={{ fontSize: '1rem' }}>
                    ※ 此為粗估參考值，建議與顧問進一步討論
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
